import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import CreateThreadModal from './CreateThreadModal'
import EditThreadModal from './EditThreadModal'
import ReportModal from './ReportModal'
import { useModerator } from '@/hooks/useModerator'

interface GroupThreadsProps {
  groupId: string
  onThreadSelect: (threadId: string | null) => void
  selectedThreadId: string | null
}

interface Thread {
  id: string
  title: string
  body: string
  created_at: string
  author_id: string | null
  profiles: {
    display_name: string
  } | null
  comment_count: number
}

export default function GroupThreads({ 
  groupId, 
  onThreadSelect, 
  selectedThreadId 
}: GroupThreadsProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null)
  const [reportingThreadId, setReportingThreadId] = useState<string | null>(null)
  const isModerator = useModerator()
  const queryClient = useQueryClient()

  const { data: threads, isLoading } = useQuery({
    queryKey: ['group-threads', groupId],
    queryFn: async () => {
      const { data: threadsData, error } = await supabase
        .from('threads')
        .select(`
          id,
          title,
          body,
          created_at,
          author_id,
          profiles (
            display_name
          )
        `)
        .eq('group_id', groupId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Get comment counts and transform data
      const threadsWithComments = await Promise.all(
        (threadsData || []).map(async (thread: any) => {
          const { count } = await supabase
            .from('comments')
            .select('*', { count: 'exact', head: true })
            .eq('thread_id', thread.id)
            .eq('is_hidden', false)

          // Transform data: Supabase returns profiles as array, extract first element
          return {
            ...thread,
            profiles: Array.isArray(thread.profiles)
              ? (thread.profiles[0] || null)
              : thread.profiles,
            comment_count: count || 0,
          }
        })
      )

      return threadsWithComments as Thread[]
    },
  })

  // Select first thread by default if none selected
  useEffect(() => {
    if (threads && threads.length > 0 && !selectedThreadId) {
      onThreadSelect(threads[0].id)
    }
  }, [threads, selectedThreadId, onThreadSelect])

  const handleCreateThread = () => {
    setShowCreateModal(true)
  }

  const handleThreadCreated = (threadId: string) => {
    setShowCreateModal(false)
    // Select the newly created thread
    onThreadSelect(threadId)
  }

  const deleteThread = useMutation({
    mutationFn: async (threadId: string) => {
      // First, delete all comments for this thread
      const { error: commentsError } = await supabase
        .from('comments')
        .delete()
        .eq('thread_id', threadId)

      if (commentsError) throw commentsError

      // Then delete the thread itself
      const { error } = await supabase
        .from('threads')
        .delete()
        .eq('id', threadId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-threads', groupId] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
    },
  })

  const handleEditThread = (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingThreadId(threadId)
  }

  const handleDeleteThread = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Are you sure you want to delete this thread? This action cannot be undone.')) {
      try {
        await deleteThread.mutateAsync(threadId)
        // If deleted thread was selected, clear selection
        if (selectedThreadId === threadId) {
          onThreadSelect(null)
        }
      } catch (err: any) {
        alert(err.message || 'Failed to delete thread')
      }
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 
          className="text-2xl font-bold"
          style={{ color: 'var(--text-primary)' }}
        >
          Threads
        </h2>
        <button
          onClick={handleCreateThread}
          className="px-4 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          Create Thread
        </button>
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--text-primary)' }}>Loading threads...</p>
      ) : !threads || threads.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          No threads yet. Be the first to create one!
        </p>
      ) : (
        <div className="space-y-4">
          {threads.map((thread) => {
            const createdDate = format(new Date(thread.created_at), 'dd/MM/yyyy')
            const authorName = thread.profiles?.display_name || 'Unknown'
            const isSelected = selectedThreadId === thread.id

            return (
              <div
                key={thread.id}
                onClick={() => onThreadSelect(thread.id)}
                className="p-4 rounded border-2 cursor-pointer"
                style={{
                  backgroundColor: isSelected
                    ? 'var(--bg-tertiary)'
                    : 'var(--bg-secondary)',
                  borderColor: 'var(--bg-tertiary)',
                  borderLeftWidth: isSelected ? 4 : 2,
                  borderLeftStyle: 'solid',
                  borderLeftColor: isSelected ? 'var(--color-focus)' : 'var(--bg-tertiary)',
                }}
                aria-current={isSelected ? 'true' : undefined}
                aria-label={isSelected ? `Thread: ${thread.title} (selected). Comments shown on the right.` : `Thread: ${thread.title}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 
                      className="text-lg font-bold mb-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {thread.title}
                    </h3>
                    <p 
                      className="mb-3"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {thread.body.length > 100 
                        ? `${thread.body.substring(0, 100)}...` 
                        : thread.body}
                    </p>
                    <div 
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <span>By {authorName}</span>
                      <span className="ml-4">{thread.comment_count} comments</span>
                      <span className="ml-4">{createdDate}</span>
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setReportingThreadId(thread.id)
                      }}
                      className="px-3 py-1 text-sm font-semibold rounded"
                      style={{
                        backgroundColor: 'var(--color-error)',
                        color: 'var(--text-inverse)',
                      }}
                      aria-label={`Report thread: ${thread.title}`}
                    >
                      Report
                    </button>
                    {isModerator && (
                      <>
                        <button
                          onClick={(e) => handleEditThread(thread.id, e)}
                          className="px-3 py-1 text-sm font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--bg-interactive)',
                            color: 'var(--text-inverse)',
                          }}
                          aria-label={`Edit thread: ${thread.title}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => handleDeleteThread(thread.id, e)}
                          className="px-3 py-1 text-sm font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--color-error)',
                            color: 'var(--text-inverse)',
                          }}
                          aria-label={`Delete thread: ${thread.title}`}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateThreadModal
          groupId={groupId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleThreadCreated}
        />
      )}

      {editingThreadId && (() => {
        const thread = threads?.find(t => t.id === editingThreadId)
        if (!thread) return null
        return (
          <EditThreadModal
            threadId={thread.id}
            currentTitle={thread.title}
            currentBody={thread.body}
            onClose={() => setEditingThreadId(null)}
            onSuccess={() => {
              setEditingThreadId(null)
            }}
          />
        )
      })()}

      {reportingThreadId && (
        <ReportModal
          targetType="thread"
          targetId={reportingThreadId}
          onClose={() => setReportingThreadId(null)}
          onSuccess={() => {
            setReportingThreadId(null)
            alert('Report submitted successfully. Thank you for helping keep our community safe.')
          }}
        />
      )}
    </div>
  )
}

